const DEV_PROXY_BASE = '/api';
const configuredBase = import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, '');
const AUTH_STORAGE_KEY = '254tracks.auth';

function resolveDefaultApiBaseUrl(): string {
  if (configuredBase) {
    return configuredBase;
  }

  if (import.meta.env?.DEV) {
    return DEV_PROXY_BASE;
  }

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8888';
    }
  }

  return DEV_PROXY_BASE;
}

export const API_BASE_URL = resolveDefaultApiBaseUrl();

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

type ApiRequestOptions = RequestInit & { authToken?: string };

let isRedirectingForExpiredSession = false;

function isExpiredTokenPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const detail = 'detail' in payload ? String((payload as Record<string, unknown>).detail ?? '') : '';
  const error = 'error' in payload ? String((payload as Record<string, unknown>).error ?? '') : '';
  return detail.toLowerCase().includes('expired token') || error.toLowerCase().includes('expired token');
}

function redirectToSignIn() {
  if (typeof window === 'undefined' || isRedirectingForExpiredSession) {
    return;
  }

  isRedirectingForExpiredSession = true;
  localStorage.removeItem(AUTH_STORAGE_KEY);

  const rawBasePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '';
  const basePath = rawBasePath === '/' ? '' : rawBasePath.replace(/\/+$/, '');
  const path = window.location.pathname;
  const isAlreadyOnSignIn = path === `${basePath}/signin` || path === `${basePath}/login`;
  const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const search = new URLSearchParams();
  if (!isAlreadyOnSignIn) {
    search.set('from', nextPath);
  }
  search.set('expired', '1');

  const redirectUrl = new URL(`${basePath}/signin`, window.location.origin);
  redirectUrl.search = search.toString();
  window.location.assign(redirectUrl.toString());
}

export async function apiRequest<T>(
  path: string,
  { authToken, headers, ...init }: ApiRequestOptions = {}
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const requestHeaders = new Headers(headers);
  if (authToken) {
    requestHeaders.set('Authorization', `Bearer ${authToken}`);
  }
  const hasBody = init.body !== undefined && init.body !== null;
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (hasBody && !isFormData && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    ...init,
    headers: requestHeaders,
    credentials: init.credentials ?? 'include',
  });

  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload && 'error' in payload
        ? String((payload as Record<string, unknown>).error)
        : response.statusText || 'Request failed';
    if (isExpiredTokenPayload(payload)) {
      redirectToSignIn();
    }
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}
